import type { ConsoleUser, PlatformUser, PlatformUserApiResponse, UserInfo, UserListResponse } from '../../stores/auth'
import { demoApi } from './db'

interface V1User {
	id?: string
	name?: string
	email: string
	role?: {
		tenant_id?: string | number
		name?: string
	}
	roles?: number[]
}

export function mapResponseToPlatformUser(response: PlatformUserApiResponse): PlatformUser {
	const tenantIds = response.tenants.map(t => t.id)
	const role = response.user.role
		? {
				...response.user.role,
				permissions: response.user.role.permissions,
			}
		: null
	return {
		name: response.user.name,
		email: response.user.email,
		tenants: tenantIds,
		tenant_list: response.tenants,
		tenant_id: response.user.role?.tenant_id || tenantIds[0],
		role,
		organizations: response.organizations,
		'tenant-settings': response.tenant_settings,
	}
}

export const usersApi = {
	getCurrentUser: async (_tenantId?: string): Promise<ConsoleUser | PlatformUser> => {
		const response = await demoApi.get<PlatformUserApiResponse>('/me')
		return mapResponseToPlatformUser(response)
	},

	getUsers: async (organizationId?: string): Promise<UserListResponse> => {
		if (organizationId) {
			const res = await demoApi.getNoTenant<{
				users: Array<{ tenant_id: string; name: string; email: string; role_name: string }>
			}>(`/v3/organizations/${encodeURIComponent(organizationId)}/users`)

			return {
				users: (res.users ?? []).map(user => ({
					name: user.name,
					email: user.email,
					role_name: user.role_name,
					tenantId: user.tenant_id,
				})),
			}
		}

		const res = await demoApi.get<
			| V1User[]
			| { result: V1User[] }
			| {
					result: Record<
						string,
						{
							name: string
							email: string
							roles: number[]
							role_name?: string
							userId?: string
							tenantId?: string
							tenantName?: string
						}
					>
			  }
		>('/v1/users')

		if (Array.isArray(res)) {
			return {
				users: res.map((u: V1User) => ({
					name: u.name || u.email,
					email: u.email,
					role_name: u.role?.name || '',
				})),
			}
		}

		const raw = res.result
		const users =
			raw && typeof raw === 'object' && !Array.isArray(raw)
				? Object.entries(raw).map(([id, u]) => ({
						id,
						name: u.name || u.email,
						email: u.email,
						role_name: u.role_name ?? (u.roles?.length ? String(u.roles[0]) : ''),
						tenantName: u.tenantName,
					}))
				: Array.isArray(raw)
					? raw.map((u: V1User) => ({
							id: (u as V1User & { id?: string }).id,
							name: u.name || u.email,
							email: u.email,
							role_name: u.role?.name || '',
						}))
					: []
		return { users: users as UserInfo[] }
	},

	createUser: async (userData: Partial<UserInfo>, tenantId?: string): Promise<{ status: string }> => {
		const options: RequestInit = {}
		if (tenantId) {
			options.headers = {
				'x-bytebeam-tenant': tenantId,
			}
		}
		await demoApi.post<{ status: string }>('/v1/users', userData, options)
		return { status: 'success' }
	},

	inviteUser: async (organizationId: string, email: string, roleId: string): Promise<{ status: string }> =>
		demoApi.post(`/v1/users/${organizationId}/invite`, { email, roleId }),

	removeUser: async (organizationId: string, userId: string): Promise<void> =>
		demoApi.delete(`/v1/users/${organizationId}/${userId}`),

	updateUser: async (userEmail: string, roleName: string, _tenantId?: string): Promise<{ status: string }> => {
		const usersRes = await demoApi.get<
			| { result: V1User[] }
			| { result: Record<string, { name: string; email: string; roles: number[]; userId?: string }> }
		>('/v1/users')
		const raw = usersRes.result
		const entry = Array.isArray(raw)
			? (raw.find((u: V1User) => u.email === userEmail) as (V1User & { id?: string }) | undefined)
			: raw && typeof raw === 'object'
				? Object.entries(raw).find(([, u]) => u.email === userEmail)
				: undefined
		const userId = entry
			? Array.isArray(entry)
				? ((entry[1] as { userId?: string })?.userId ?? entry[0])
				: (entry as V1User).id
			: undefined
		if (!userId) {
			throw new Error(`User with email ${userEmail} not found`)
		}
		await demoApi.put(`/v1/users/${encodeURIComponent(userId)}`, { role: { name: roleName } })
		return { status: 'success' }
	},
	deleteUser: async (userEmail: string, tenantId?: string): Promise<{ status: string }> => {
		const options: RequestInit = {}
		if (tenantId) {
			options.headers = {
				'x-bytebeam-tenant': tenantId,
			}
		}
		await demoApi.delete<{ status: string }>('/v1/users/by-email', { email: userEmail }, options)
		return { status: 'success' }
	},
}
